import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PrayerTimeEntry } from '../services/prayerTimesApi';

export type TimeOfDay = 'fajr' | 'sunrise' | 'midday' | 'asr' | 'maghrib' | 'night';

export interface AmbientPersonalization {
  overlayColor: string;
  greetingSubline: string;
  focusHighlightStart: number;
  focusHighlightEnd: number;
  timeOfDay: TimeOfDay;
}

interface Props {
  focus: string;
  focusCompleted: boolean;
  prayerTimes: PrayerTimeEntry[];
}

function getTimeOfDay(hour: number, minute: number): TimeOfDay {
  const t = hour * 60 + minute;
  if (t >= 210 && t < 330) return 'fajr';     // 3:30–5:30am
  if (t >= 330 && t < 600) return 'sunrise';   // 5:30–10am
  if (t >= 600 && t < 840) return 'midday';    // 10am–2pm
  if (t >= 840 && t < 1020) return 'asr';      // 2–5pm
  if (t >= 1020 && t < 1170) return 'maghrib'; // 5–7:30pm
  return 'night';
}

function getBaseOverlay(tod: TimeOfDay): [number, number, number, number] {
  // Returns [r, g, b, a]
  switch (tod) {
    case 'fajr':    return [67, 56, 202, 0.35];
    case 'sunrise': return [180, 120, 30, 0.22];
    case 'midday':  return [0, 0, 0, 0.42];
    case 'asr':     return [71, 85, 105, 0.32];
    case 'maghrib': return [194, 65, 12, 0.28];
    case 'night':   return [15, 23, 42, 0.52];
  }
}

function getMinutesUntilNextPrayer(prayerTimes: PrayerTimeEntry[]): number {
  if (!prayerTimes.length) return Infinity;
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  // Use the locale-independent `minutes` field — never the localized `time` string,
  // whose digits/AM-PM markers vary by locale and aren't reliably parseable.
  for (const p of prayerTimes) {
    if (p.minutes > nowMinutes) return p.minutes - nowMinutes;
  }
  // All prayers passed — wrap to Fajr tomorrow
  const fajr = prayerTimes[0];
  return fajr ? 24 * 60 - nowMinutes + fajr.minutes : Infinity;
}

export function useAmbientPersonalization({ focus, focusCompleted, prayerTimes }: Props): AmbientPersonalization {
  const { t } = useTranslation(['prayer']);
  // Re-compute every minute so time-based logic stays current
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  return useMemo(() => {
    const now = new Date();
    const hour = now.getHours();
    const minute = now.getMinutes();
    const tod = getTimeOfDay(hour, minute);
    const minutesUntilPrayer = getMinutesUntilNextPrayer(prayerTimes);
    const prayerSoon = minutesUntilPrayer <= 30;
    const prayerVeryClose = minutesUntilPrayer <= 15;
    const trimmedFocus = focus.trim();

    // Overlay colour — boost alpha by 0.08 when prayer is near
    const [r, g, b, a] = getBaseOverlay(tod);
    const alpha = prayerSoon ? Math.min(a + 0.08, 0.8) : a;
    const overlayColor = `rgba(${r}, ${g}, ${b}, ${alpha})`;

    // Greeting subline + focus highlight indices.
    // Lines that embed the user's focus are interpolated with {{focus}}; the
    // highlight range is derived from where that focus text lands in the
    // resolved (translatable) string, so it survives reordering across locales.
    let greetingSubline = t('prayer:ambient.default');
    let focusHighlightStart = -1;
    let focusHighlightEnd = -1;

    const withFocusHighlight = (key: string) => {
      greetingSubline = t(key, { focus: trimmedFocus });
      const idx = greetingSubline.indexOf(trimmedFocus);
      if (idx >= 0) {
        focusHighlightStart = idx;
        focusHighlightEnd = idx + trimmedFocus.length;
      }
    };

    if (trimmedFocus && prayerSoon) {
      greetingSubline = t('prayer:ambient.prayerApproaches');
    } else if (trimmedFocus && (tod === 'fajr' || (hour >= 5 && hour < 6))) {
      withFocusHighlight('prayer:ambient.morningPursue');
    } else if (trimmedFocus && tod === 'maghrib') {
      withFocusHighlight('prayer:ambient.dayCloses');
    } else if (trimmedFocus && focusCompleted) {
      greetingSubline = t('prayer:ambient.fulfilled');
    } else if (trimmedFocus) {
      withFocusHighlight('prayer:ambient.workingTowards');
    } else if (prayerVeryClose) {
      greetingSubline = t('prayer:ambient.prayerNear');
    }

    return { overlayColor, greetingSubline, focusHighlightStart, focusHighlightEnd, timeOfDay: tod };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, focusCompleted, prayerTimes, t, /* tick triggers recompute */]);
}
